import {useQuery} from '@tanstack/react-query';
import {api} from '@/shared/services/api-client';
import {queryKeys} from '@/lib/query-keys';
import type {HistoryEntry} from '@/shared/types/history';

/**
 * Fetch a participant's change-log. `scheduleId` scopes to a single session
 * (Booked Sessions "Session History" tab); omit it for the full participant log.
 * The endpoint already returns entries newest-first.
 *
 * Lives in `shared` so both the participant tab (shared dialog) and the session
 * tab (scheduler feature) can use it without a cross-feature import.
 */
export async function fetchParticipantHistory(
  participantId: string,
  projectId: string,
  scheduleId?: string,
): Promise<HistoryEntry[]> {
  const res = await api.participants.getHistory(participantId, projectId, scheduleId);
  const data = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(data) ? (data as HistoryEntry[]) : [];
}

export function useParticipantHistoryQuery(params: {
  participantId?: string | null;
  projectId?: string | null;
  scheduleId?: string;
  enabled?: boolean;
}) {
  const {participantId, projectId, scheduleId, enabled = true} = params;
  return useQuery({
    queryKey: queryKeys.history.forParticipant(
      String(participantId ?? ''),
      String(projectId ?? ''),
      scheduleId,
    ),
    queryFn: () =>
      fetchParticipantHistory(String(participantId), String(projectId), scheduleId),
    enabled: Boolean(enabled && participantId && projectId),
    staleTime: 30_000,
    // Avoid flicker: history is append-only and invalidated on mutations, so a
    // background refetch on window-focus only risks a transient loading flash.
    refetchOnWindowFocus: false,
  });
}
